// src/features/estoque/components/ListaEstoque.jsx
import React, { useState, useEffect } from "react";
import { 
  observarEstoque, 
  fecharTemporadaEstoque, 
  obterTemporadasFechadas 
} from "services/estoqueService";
import { useUser } from "hooks/useUser";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { Search, PackageOpen, Archive, Lock } from "lucide-react";
import toast from "react-hot-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";

/**
 * Componente que exibe a lista de estoque atualizada em tempo real (onSnapshot).
 */
const ListaEstoque = () => {
  const { isAdmin } = useUser();
  const [estoqueAtual, setEstoqueAtual] = useState([]);
  const [itensExibidos, setItensExibidos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  
  // Temporadas
  const [temporadas, setTemporadas] = useState([]);
  const [temporadaSelecionada, setTemporadaSelecionada] = useState("atual");
  
  // Modal de Fechamento
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [nomeTemporada, setNomeTemporada] = useState("");
  const [isClosing, setIsClosing] = useState(false);

  // Carregar histórico de temporadas
  const carregarTemporadas = async () => {
    const historico = await obterTemporadasFechadas();
    setTemporadas(historico);
  };

  useEffect(() => {
    carregarTemporadas();
    
    // Inicia o listener do estoque atual
    const unsubscribe = observarEstoque((dadosAtualizados) => {
      setEstoqueAtual(dadosAtualizados);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Efeito para alternar os itens exibidos dependendo da temporada selecionada
  useEffect(() => {
    if (temporadaSelecionada === "atual") {
      setItensExibidos(estoqueAtual);
    } else {
      const temp = temporadas.find(t => t.id === temporadaSelecionada);
      if (temp) {
        setItensExibidos(temp.itens || []);
      }
    }
  }, [temporadaSelecionada, estoqueAtual, temporadas]);

  const handleFecharTemporada = async (e) => {
    e.preventDefault();
    if (!nomeTemporada.trim()) {
      toast.error("O nome da temporada é obrigatório.");
      return;
    }
    
    setIsClosing(true);
    const result = await fecharTemporadaEstoque(nomeTemporada.trim());
    setIsClosing(false);
    
    if (result.success) {
      toast.success(result.message);
      setIsModalOpen(false);
      setNomeTemporada("");
      carregarTemporadas(); // Atualiza o dropdown
      setTemporadaSelecionada("atual");
    } else {
      toast.error(result.message);
    }
  };

  const estoqueFiltrado = itensExibidos.filter((item) =>
    item.nome.toLowerCase().includes(filtro.toLowerCase()) || 
    (item.codigo && item.codigo.toLowerCase().includes(filtro.toLowerCase()))
  );

  const isHistorico = temporadaSelecionada !== "atual";

  const getStockClasses = (qty, isHist) => {
    if (isHist) return 'bg-gray-100 text-gray-800 dark:bg-accent dark:text-gray-200';
    if (qty < 0) return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400 border border-red-500/50';
    if (qty <= 10) return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400';
    return 'bg-gray-100 text-gray-800 dark:bg-accent dark:text-gray-200';
  };

  return (
    <div className="bg-transparent md:bg-white md:dark:bg-card md:shadow-sm md:rounded-lg md:border md:border-gray-200 md:dark:border-border p-1 md:p-6 flex flex-col mt-4 md:mt-6">
      
      {/* Cabeçalho e Controles */}
      <div className="flex flex-col xl:flex-row xl:items-center justify-between mb-6 gap-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
            <PackageOpen className="w-5 h-5 text-green-600 dark:text-muted-foreground" />
            {isHistorico ? "Histórico de Estoque" : "Estoque Atual"}
        </h2>
        
        <div className="flex flex-col md:flex-row gap-3 items-center w-full xl:w-auto">
          {/* Select de Temporada */}
          <div className="w-full md:w-56">
            <Select value={temporadaSelecionada} onValueChange={setTemporadaSelecionada}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione a Temporada" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="atual" className="font-bold text-green-600 dark:text-muted-foreground">
                  Ao Vivo (Temporada Atual)
                </SelectItem>
                {temporadas.map((temp) => (
                  <SelectItem key={temp.id} value={temp.id}>
                    {temp.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Input de Busca */}
          <div className="relative w-full md:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Buscar por código ou nome..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="pl-9 w-full"
            />
          </div>

          {/* Botão de Fechar Temporada (Apenas Admin e na temporada atual) */}
          {isAdmin && !isHistorico && (
            <Button 
              variant="outline" 
              className="w-full md:w-auto text-orange-600 border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-orange-800 dark:text-orange-400 dark:hover:bg-accent"
              onClick={() => setIsModalOpen(true)}
            >
              <Archive className="w-4 h-4 mr-2" />
              Zerar / Nova Temporada
            </Button>
          )}
        </div>
      </div>

      {loading && temporadaSelecionada === "atual" ? (
        <div className="space-y-4 animate-pulse">
            <div className="h-10 bg-gray-200 dark:bg-secondary rounded w-full"></div>
            <div className="h-10 bg-gray-200 dark:bg-secondary rounded w-full"></div>
            <div className="h-10 bg-gray-200 dark:bg-secondary rounded w-full"></div>
        </div>
      ) : estoqueFiltrado.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-border rounded-lg">
            Nenhum produto encontrado.
        </div>
      ) : (
        <>
          {/* Visão Mobile (Cards) */}
          <div className="md:hidden space-y-4">
            {estoqueFiltrado.map((item) => (
              <div key={item.id} className="bg-white dark:bg-card border border-gray-200 dark:border-border rounded-lg p-4 shadow-sm flex flex-col gap-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="overflow-hidden">
                    <p className="font-semibold text-base text-gray-900 dark:text-gray-100 truncate">{item.nome}</p>
                    <p className="text-sm font-mono text-gray-500">Cód: {item.codigo}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className={`px-2 py-1 rounded font-bold text-sm ${getStockClasses(item.quantidadeAtual, isHistorico)}`}>
                      {item.quantidadeAtual}
                    </span>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm pt-3 border-t border-gray-100 dark:border-border text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">NCM:</span>
                    {item.ncm}
                  </div>
                  {!isHistorico && (
                    <div className="flex items-center gap-1">
                      <span className="font-medium">Atualizado:</span>
                      {item.ultimaAtualizacao}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Visão Desktop (Tabela) */}
          <div className="hidden md:block overflow-x-auto rounded-md border border-gray-200 dark:border-border">
            <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-accent dark:text-gray-300">
                <tr>
                  <th scope="col" className="px-6 py-3 font-semibold">Código</th>
                  <th scope="col" className="px-6 py-3 font-semibold">Produto</th>
                  <th scope="col" className="px-6 py-3 font-semibold text-center">NCM</th>
                  <th scope="col" className="px-6 py-3 font-semibold text-right">Qtd. {isHistorico ? "Final" : "Atual"}</th>
                  {!isHistorico && <th scope="col" className="px-6 py-3 font-semibold text-right">Última Atualização</th>}
                </tr>
              </thead>
              <tbody>
                {estoqueFiltrado.map((item) => (
                  <tr 
                      key={item.id} 
                      className="bg-white border-b dark:bg-card dark:border-border hover:bg-gray-50 dark:hover:bg-accent transition-colors"
                  >
                    <td className="px-6 py-4 font-mono text-xs">{item.codigo}</td>
                    <td className="px-6 py-4 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                      {item.nome}
                    </td>
                    <td className="px-6 py-4 font-mono text-center">
                      {item.ncm}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span className={`px-2 py-1 rounded font-bold ${getStockClasses(item.quantidadeAtual, isHistorico)}`}>
                          {item.quantidadeAtual}
                      </span>
                    </td>
                    {!isHistorico && (
                      <td className="px-6 py-4 text-right text-xs text-gray-400">
                        {item.ultimaAtualizacao}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal de Fechamento de Temporada */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent aria-describedby={undefined} className="w-full h-[100dvh] max-h-none sm:h-auto sm:max-h-[85vh] sm:max-w-md p-0 border-0 sm:border rounded-none sm:rounded-lg overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b shrink-0 bg-white dark:bg-background">
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-orange-500" />
            Zerar Estoque
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleFecharTemporada} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 py-4 grid gap-4 content-start">
            <p className="text-sm text-muted-foreground">
              Esta ação irá salvar as quantidades atuais no histórico e zerar a contagem do estoque físico de <strong>todos</strong> os produtos. Essa operação não pode ser desfeita.
            </p>
            <div className="grid gap-2">
              <Label htmlFor="nomeTemporada">Nome da Temporada que está se encerrando</Label>
              <Input
                id="nomeTemporada"
                placeholder="Ex: Alta Temporada Janeiro 2026"
                value={nomeTemporada}
                onChange={(e) => setNomeTemporada(e.target.value)}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-gray-50 dark:bg-card mt-auto gap-2 flex-col-reverse sm:flex-row">
            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} disabled={isClosing} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button type="submit" variant="destructive" disabled={isClosing} className="w-full sm:w-auto">
              {isClosing ? "Processando..." : "Confirmar e Zerar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListaEstoque;
